/**
 * ============================================================================
 * useSignAttestationMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/vendor-evaluations/{id}/sign — close out a vendor
 *          evaluation with the appropriate signature(s).
 *
 * REGULATORY: VND-007 routes DISQUALIFIED through DUAL signatures plus a
 *  rationale ≥200 chars. APPROVED and CONDITIONAL are single-signer.
 *  We pass a single `signature_bundle_ids` array regardless — the server
 *  enforces the right shape and the rationale-length guard.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { VendorEvaluation } from './useEvaluationQuery';

export interface SignAttestationInput {
  evaluationId: string;
  /** One id for APPROVED/CONDITIONAL; two distinct ids for DISQUALIFIED. */
  signature_bundle_ids: string[];
  /** Required and ≥200 chars when classification is DISQUALIFIED. */
  rationale?: string;
}

export function useSignAttestationMutation(): UseMutationResult<
  VendorEvaluation,
  Error,
  SignAttestationInput
> {
  return useMutation<VendorEvaluation, Error, SignAttestationInput>({
    mutationFn: ({ evaluationId, ...body }) =>
      apiFetch<VendorEvaluation>(
        `/api/v1/vendor-evaluations/${encodeURIComponent(evaluationId)}/sign`,
        {
          method: 'POST',
          body,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
